import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import BoxContext from '../context/BoxContext';
import logo from '../assets/logo.jpg';

/**
 * Header component con animaciones mejoradas.
 * Representa la barra de navegación principal del sitio.
 */
export default function Header() {
  // Estado para el menú móvil
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // Obtenemos datos de la caja desde el contexto
  const { boxCount, toggleBox } = useContext(BoxContext);

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: 'spring', stiffness: 100 }}
      className="bg-primary text-white shadow-md sticky top-0 z-50"
    >
      <div className="container mx-auto px-4 py-3 flex items-center justify-between">
        {/* Logo / Marca */}
        <Link to="/" className="flex items-center space-x-3">
          <motion.img
            whileHover={{ scale: 1.05 }}
            src={logo}
            alt="Su Carne Logo"
            className="h-12 w-12 md:h-14 md:w-14 rounded-full object-cover shadow-lg"
          />
          <motion.span
            whileHover={{ scale: 1.05 }}
            className="text-2xl font-extrabold tracking-wide uppercase"
          >
            Su&nbsp;Carne
          </motion.span>
        </Link>

        {/* Navegación desktop */}
        <nav className="hidden md:flex items-center space-x-6">
          <Link to="/" className="hover:text-secondary transition-colors">Inicio</Link>
          <a href="#combos" className="hover:text-secondary transition-colors">Combos</a>
          <a href="#box" className="hover:text-secondary transition-colors">Parrilla&nbsp;Box</a>
          <a href="#contacto" className="hover:text-secondary transition-colors">Contacto</a>

          {/* Icono de la caja con contador animado */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={toggleBox}
            className="relative ml-4 text-3xl cursor-pointer focus:outline-none"
            title="Ver caja"
          >
            <motion.span
              animate={boxCount > 0 ? { rotate: [0, -10, 10, -10, 0] } : {}}
              transition={{ duration: 0.5 }}
              role="img"
              aria-label="box"
            >
              📦
            </motion.span>
            <AnimatePresence>
              {boxCount > 0 && (
                <motion.span
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 15 }}
                  className="absolute -top-2 -right-3 bg-white text-primary text-xs font-semibold w-6 h-6 rounded-full flex items-center justify-center shadow-lg"
                >
                  {boxCount}
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>
        </nav>

        {/* Botón móvil */}
        <div className="md:hidden flex items-center gap-3">
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={toggleBox}
            className="relative text-2xl focus:outline-none"
          >
            📦
            {boxCount > 0 && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-1 -right-2 bg-white text-primary text-xs font-semibold w-5 h-5 rounded-full flex items-center justify-center"
              >
                {boxCount}
              </motion.span>
            )}
          </motion.button>
          <button
            className="focus:outline-none"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Menú"
          >
            <motion.div
              animate={isMenuOpen ? { rotate: 45 } : { rotate: 0 }}
              className="relative w-6 h-5"
            >
              <span className="block w-6 h-0.5 bg-white mb-1"></span>
              <span className="block w-6 h-0.5 bg-white mb-1"></span>
              <span className="block w-6 h-0.5 bg-white"></span>
            </motion.div>
          </button>
        </div>
      </div>

      {/* Menú móvil desplegable con animación */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-primary-dark text-white overflow-hidden"
          >
            <ul className="flex flex-col space-y-2 p-4">
              <motion.li
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
              >
                <Link to="/" onClick={() => setIsMenuOpen(false)}>Inicio</Link>
              </motion.li>
              <motion.li
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <a href="#combos" onClick={() => setIsMenuOpen(false)}>Combos</a>
              </motion.li>
              <motion.li
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <a href="#box" onClick={() => setIsMenuOpen(false)}>Parrilla&nbsp;Box</a>
              </motion.li>
              <motion.li
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <a href="#contacto" onClick={() => setIsMenuOpen(false)}>Contacto</a>
              </motion.li>
            </ul>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}